import { MDXProvider } from '@mdx-js/react'
import Layout from 'components/Layout'
import { graphql } from 'gatsby'
import { MDXRenderer } from 'gatsby-plugin-mdx'
import React from 'react'
import { Check as CheckIcon, Close2 as CloseIcon } from '../components/Icons'
import Hero from 'components/ProductLayout/Hero'
import { MainFeatures } from 'components/ProductLayout/Feature'
import Testimonial from 'components/ProductLayout/Testimonial'
import Comparison from 'components/ProductLayout/Comparison'
import BlogPosts from 'components/ProductLayout/BlogPosts'
import Roadmap from 'components/ProductLayout/Roadmap'
import PairsWith from 'components/ProductLayout/PairsWith'
import { SectionWrapper } from 'components/Sections'
import Documentation from 'components/ProductLayout/Documentation'
import { ProductLayout } from 'components/ProductLayout'
import Tutorials from 'components/ProductLayout/Tutorials'
import { PlanComparison } from 'components/Pricing/PlanComparison'
import Questions from 'components/ProductLayout/Questions'
import Customers from 'components/ProductLayout/Customers'
import SampleFlow from 'components/ProductLayout/SampleFlow'
import ProductSlider from 'components/ProductLayout/ProductSlider'
import HeroCondensed from 'components/ProductLayout/HeroCondensed'
import { shortcodes } from '../mdxGlobalComponents'
import { SEO } from 'components/seo'
import { SEO2 } from 'components/seo2'
import Link from 'components/Link'
import { StaticImage } from 'gatsby-plugin-image'
import PostLayout from 'components/PostLayout'
import { Platform } from 'components/NotProductIcons'
import { getSeoImage } from 'utils/googleData'
import { PropertyManagement, RealestateMarketing, RealestateCrm, Websites } from 'components/ProductIcons'

const Check = (props: any) => <CheckIcon {...props} className="mx-auto w-5" />
const Close = (props: any) => <CloseIcon {...props} className="mx-auto w-5" />

export default function Product(pageProps) {
    const { data, location, pageContext, params, children } = pageProps
    const { pageData, blogPosts, documentation, tutorials, customers } = data
    const {
        body,
        excerpt,
        parent,
        fields: { slug },
    } = pageData
    const {
        title,
        subtitle,
        heroDescription,
        description,
        showNav,
        showFooter,
        productFeatures,
        productSections,
        productTestimonial,
        productTeam,
        productMainCTA,
        productPricingCTA,
        productPairsWith,
        productHero,
        sampleFlow,
        productSliderSettings,
        productSlides,
        productDocumentation,
        productBlog,
        seoTitle,
        seoDescription,
        seoimage,
    } = pageData?.frontmatter
    const heroImgObject = productHero?.image?.childImageSharp ? productHero : parent?.cover
    const defaultProductBlogTitle = `Blog posts that mention ${title}`
    const components = {
        ...shortcodes(pageProps),
        Hero: (props: any) => (
            <Hero
                {...props}
                googleImages={[parent?.images, parent?.images2]}
                customers={customers}
                svgImage={productHero?.svgImage}
            />
        ),
        HeroCondensed: (props: any) => (
            <HeroCondensed
                {...props}
                mainCTA={productMainCTA}
                pricingCTA={productPricingCTA}
                googleImages={[parent?.images, parent?.images2]}
                title={title}
                subtitle={subtitle}
                image={productHero}
            />
        ),
        ProductSlider: (props: any) => (
            <ProductSlider
                {...props}
                productSlides2={productSlides}
                productSliderSettings2={productSliderSettings}
                googleImages={[parent?.images, parent?.images2]}
                title={title}
            />
        ),
        MainFeatures: (props: any) => (
            <MainFeatures {...props} googleImages={[parent?.images, parent?.images2]} features2={productFeatures} />
        ),
        Testimonial: (props: any) => (
            <Testimonial
                {...props}
                googleImages={[parent?.images, parent?.images2]}
                productTestimonial2={productTestimonial}
            />
        ),
        Check,
        Close,
        // SampleFlow: (props: any) => <SampleFlow {...props} {...sampleFlow}  />,
        Comparison: (props: any) => (
            <Comparison
                {...props}
                googleImages={[parent?.images, parent?.images2]}
                description={`How does iCloudReady ${title} compare?`}
            />
        ),
        BlogPosts: (props: any) => (
            <BlogPosts
                {...props}
                title={productBlog?.title || defaultProductBlogTitle}
                googleImages={[parent?.images, parent?.images2]}
                posts={blogPosts?.edges}
            />
        ),
        Roadmap: (props: any) => (
            <Roadmap
                {...props}
                team={productTeam}
                googleImages={[parent?.images, parent?.images2]}
                subtitle={`Here's what the ${productTeam} Team is building next.`}
            />
        ),
        PairsWith: (props: any) => (
            <PairsWith {...props} googleImages={[parent?.images, parent?.images2]} products2={productPairsWith} />
        ),
        Documentation: (props: any) => (
            // <div id="documentation">
            <SectionWrapper {...props}>
                <Documentation
                    {...props}
                    documentation={{
                        indexURL: pageContext?.documentationNav?.url,
                        pages: documentation?.nodes,
                    }}
                    googleImages={[parent?.images, parent?.images2]}
                    title={title}
                    // tutorials={tutorials?.nodes}
                />
            </SectionWrapper>
            // </div>
        ),
        Tutorials: (props: any) => (
            <Tutorials googleImages={[parent?.images, parent?.images2]} tutorials={tutorials?.nodes} />
        ),
        PlanComparison: (props: any) => (
            <div className="mx-auto max-w-screen-2xl">
                <PlanComparison {...props} googleImages={[parent?.images, parent?.images2]} showCTA={false} />
            </div>
        ),
        Questions,
        Customers: (props: any) => (
            <Customers
                {...props}
                googleImages={[parent?.images, parent?.images2]}
                initialCustomer={location.state?.customer}
                customers={customers?.nodes.sort(
                    (a, b) =>
                        pageContext?.customerURLs.indexOf(a.fields.slug) -
                        pageContext?.customerURLs.indexOf(b.fields.slug)
                )}
            />
        ),
    }
    return (
        <Layout>
            <SEO
                title={seoTitle || pageData?.frontmatter?.title + ''}
                description={seoDescription || pageData?.frontmatter?.description || pageData?.excerpt}
                image={
                    getSeoImage({ googleImages: [parent?.images, parent?.images2] }) ||
                    `/images/product/${pageData?.fields?.slug?.split('/')[1]}.png`
                }
            />
            <ProductLayout title={title} showNav={showNav} showFooter={showFooter}>
                <MDXProvider components={components}>
                    <MDXRenderer>{body}</MDXRenderer>
                </MDXProvider>
            </ProductLayout>
        </Layout>
    )
}

export const query = graphql`
    query Product(
        $id: String!
        $blogTags: [String!]!
        $tutorialTags: [String!]!
        $documentationURLs: [String!]!
        $customerURLs: [String]
    ) {
        pageData: mdx(id: { eq: $id }) {
            body
            excerpt
            fields {
                slug
            }
            frontmatter {
                title
                subtitle
                description
                seoTitle
                seoDescription
                seoimage
                productSlides {
                    label
                    url
                    image
                }
                productSliderSettings {
                    dots
                    infinite
                    arrows
                    slidesToScroll
                    autoplay
                    variableWidth
                    autoplaySpeed
                    speed
                    cssEase
                }
                showNav
                showFooter
                productTeam
                productBlog {
                    title
                }
                productMenuItems
                productHero {
                    image {
                        childImageSharp {
                            gatsbyImageData
                        }
                    }
                    width
                    height
                    svgImage
                }
                productPairsWith {
                    title
                    description
                    icon
                    url
                }
                productMainCTA {
                    title
                    subtitle
                    url
                    image {
                        childImageSharp {
                            gatsbyImageData
                        }
                    }
                    svgImage
                }
                productPricingCTA {
                    title
                    url
                }
                productTestimonial {
                    author {
                        name
                        role
                        image
                        companyName
                        companyImage
                        company {
                            name
                            image
                        }
                    }
                    quote
                    image
                    imageFile {
                        childImageSharp {
                            gatsbyImageData
                        }
                    }
                    svgImage
                }
                productFeatures {
                    title
                    description
                    icon
                }
                productSections {
                    title
                    subtitle
                    content
                    align
                    sections {
                        features {
                            title
                            description
                        }
                    }
                    features {
                        title
                        description
                    }
                    image
                    imageFrame
                    imageMaxWidth
                    imageMaxHeight
                    svgImage
                    imageFile {
                        childImageSharp {
                            gatsbyImageData
                        }
                    }
                }
            }
            parent {
                ... on GoogleDocs {
                    images {
                        name
                        publicURL
                        childImageSharp {
                            gatsbyImageData
                        }
                    }
                    forms {
                        name
                        title
                        subtitle
                        title_ar
                        subtitle_ar
                        formFields {
                            name
                            label
                            label_ar
                            component
                            enable
                            optional
                            placeholder
                            placeholder_ar
                            validation {
                                pattern
                                message
                                message_ar
                            }
                            hubspotField
                            type
                        }
                        useSampleConfig
                        formUrl
                        cta {
                            label
                            label_ar
                            url
                        }
                        confirmationMessageCTA {
                            title
                            title_ar
                            url
                        }
                        confirmationTitle {
                            title
                            title_ar
                            subtitle
                            subtitle_ar
                            description
                            description_ar
                        }
                    }
                    cover {
                        image {
                            childImageSharp {
                                gatsbyImageData
                            }
                        }
                    }
                }
            }
        }
        blogPosts: allMdx(
            filter: { fields: { slug: { regex: "/^/blog/" } }, frontmatter: { tags: { in: $blogTags } } }
            limit: 6
        ) {
            edges {
                node {
                    ...BlogFragment
                }
            }
        }
        tutorials: allMdx(
            filter: { fields: { slug: { regex: "/^/tutorials/" } }, frontmatter: { tags: { in: $tutorialTags } } }
            limit: 6
        ) {
            nodes {
                fields {
                    slug
                }
                frontmatter {
                    title
                }
            }
        }
        documentation: allMdx(filter: { fields: { slug: { in: $documentationURLs } } }) {
            nodes {
                fields {
                    slug
                }
                frontmatter {
                    title
                }
                headings {
                    depth
                    value
                }
            }
        }
        customers: allMdx(filter: { fields: { slug: { in: $customerURLs } } }) {
            nodes {
                fields {
                    slug
                }
                frontmatter {
                    customer
                    title
                    logo {
                        publicURL
                    }
                }
                body
            }
        }
    }
`
